import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_MouseInteractivity, ModuleFE_Thunderstorm, ModuleFE_Toaster, ModuleFE_WindowMessenger, mouseInteractivity_ToolTip, openContent, stopPropagation, Thunder, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import './Page_AvatarMode.scss';
import { UrlParam_UserType } from '../../consts.js';
import { TS_InputV2 } from '@nu-art/thunderstorm-frontend/components/TS_V2_Input/TS_InputV2';
import { ICONS } from '@app/styles/icons/icons';
import { JWT, ModuleFE_AvatarMode } from '../../modules/ModuleFE_AvatarMode.js';
import { SlackBuilderFE } from '@nu-art/slack-frontend/SlackBuilderFE';
import { capitalizeAllFirstLetters, filterDuplicates, formatTimestamp, ServerErrorSeverity, sortArray } from '@nu-art/ts-common';
import { ModuleFE_EHRInstallation, ModuleFE_EHRInstance, ModuleFE_Environment, ModuleFE_Facility, ModuleFE_Organization, ModuleFE_User } from '@app/org-frontend';
import { ModuleFE_Account } from '@nu-art/user-account-frontend/index';
import { ModuleFE_Value } from '@app/hcs-frontend';
import { FadeScrollWrapper, ResizableWrapper } from '@app/styles';
import { Dialog_AvatarModeFilters } from './Dialog_AvatarModeFilters.js';
import { EncounterStatus_Initial } from '@app/advisor-ui-shared';
import { QueryParam_SessionId } from '@nu-art/user-account-shared';
import { resolveValueDisplay } from '@app/hcs-shared';
import { RoutePermissions } from '@nu-art/permissions-frontend';
const AVATAR_MODE_SLACK_CHANNEL = 'avatar-mode-updates';
// Own persistence key (not shared with the EHR emulator's km_sidebar_collapsed).
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'avatar_mode_sidebar_collapsed';
const advisorBaseUrl = {
    local: 'https://localhost:8301',
    dev: 'https://quai-md-runtime-dev.firebaseapp.com',
    staging: 'https://quai-md-runtime-staging.firebaseapp.com',
    prod: 'https://quai-md-runtime.firebaseapp.com',
};
export class Page_AvatarMode extends ComponentSync {
    receiver;
    static Route = {
        key: 'avatar-mode',
        path: 'avatar-mode',
        Component: Page_AvatarMode,
        modulesToAwait: [ModuleFE_Facility],
        enabled: RoutePermissions(ModuleFE_AvatarMode.permissions.Release),
    };
    //######################### Life Cycle #########################
    __onAvatarModeFiltersChange = (selectedIds) => {
        this.setState({ selectedFacilityIds: selectedIds });
    };
    interval;
    deriveStateFromProps(nextProps, state) {
        state.search ??= '';
        state.encounters ??= [];
        state.selectedFacilityIds ??= [];
        state.activeEncountersOnly ??= false;
        state.leftPanelCollapsed ??= localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
        return state;
    }
    toggleLeftPanelCollapsed = () => {
        this.setState(prev => {
            const next = !prev.leftPanelCollapsed;
            localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
            return { leftPanelCollapsed: next };
        });
    };
    async componentDidMount() {
        this.receiver = ModuleFE_WindowMessenger
            .createReceiver(window.origin)
            .addProcessor('avatar-launched', this.onAdvisorLaunched);
        this.receiver.mount();
        try {
            await this.loadEncounters();
        }
        catch (err) {
            this.logError(err);
        }
        this.interval = setInterval(() => {
            this.forceUpdate();
        }, 10000);
    }
    componentWillUnmount() {
        this.receiver.unmount();
        clearInterval(this.interval);
    }
    //######################### Logic #########################
    async loadEncounters(encounterId) {
        try {
            const encounterIdToSearch = encounterId?.trim() !== '' ? encounterId?.trim() : undefined;
            const avatarJwts = (await ModuleFE_AvatarMode._v1.getAllEncounters({ encounterId: encounterIdToSearch }).executeSync()).avatarJwts ?? [];
            const encounters = filterDuplicates(avatarJwts.map(avatarJwt => {
                return { ...ModuleFE_AvatarMode.decodeAvatarSession(avatarJwt), jwt: avatarJwt };
            }));
            const facilityIds = filterDuplicates(encounters.map(e => e.facilityId));
            this.setState({
                encounters: encounters,
                selectedFacilityIds: facilityIds,
                facilityIds,
                search: encounterId ?? ''
            });
        }
        catch (e) {
            return ModuleFE_Toaster.toastError('Could not load encounters.');
        }
    }
    getEncounters = () => {
        let encounters = this.state.encounters;
        encounters = encounters.filter(encounter => {
            return this.state.selectedFacilityIds.includes(encounter.facilityId);
        });
        if (this.state.activeEncountersOnly) {
            encounters = encounters.filter(encounter => {
                return encounter.encounterStatus && encounter.encounterStatus !== EncounterStatus_Initial;
            });
        }
        encounters = sortArray(encounters, encounter => encounter.startTime, true);
        const search = this.state.search.trim();
        if (!search)
            return encounters;
        return encounters.filter(encounter => {
            return encounter.encounterId.includes(search);
        });
    };
    onAdvisorLaunched = () => {
        const encounter = this.state.selectedEncounter;
        if (!encounter) {
            this.logError('How was the advisor launched without a selected encounter???');
            return;
        }
        //Gather data
        const user = ModuleFE_User.getUser();
        const account = ModuleFE_Account.cache.unique(user?._id);
        const facility = ModuleFE_Facility.cache.unique(encounter?.facilityId);
        const organization = ModuleFE_Organization.cache.unique(facility?.organizationId);
        const environment = ModuleFE_Environment.cache.unique(encounter?.encounterId);
        const ehrInstallation = ModuleFE_EHRInstallation.cache.unique(environment?.ehrInstallationId);
        //Decide labels
        const userLabel = account?.email ?? 'Someone';
        const facilityText = facility?.name
            ? `*Facility:* ${facility.name} (${encounter.facilityId})`
            : `*EHR Instance ID:* ${encounter.ehrInstanceId}`;
        const environmentText = `*Environment:* ${organization?.name} - ${ehrInstallation?.label} - ${environment?.label} (${encounter.environmentId})`;
        const env = Thunder.getInstance().getEnvironment();
        // Delete and create a log to coralogix when we'll have the option
        this.logWarning(`${userLabel} just used Avatar mode on ${env}.\nEncounterId: ${encounter.encounterId}\n${facilityText}`);
        const feMessageBuilder = new SlackBuilderFE(AVATAR_MODE_SLACK_CHANNEL);
        return feMessageBuilder
            .addBlocks(SlackBuilderFE.TextSectionWithTitle(`${SlackBuilderFE.SeverityEmoji(ServerErrorSeverity.Critical)} *${userLabel} just used Avatar Mode*`, `*Using Avatar Mode on ${env}*\n`))
            .addBlocks(SlackBuilderFE.TextSection(`*Encounter Id:* ${encounter.encounterId}\n`))
            .addBlocks(SlackBuilderFE.TextSection(facilityText))
            .addBlocks(SlackBuilderFE.TextSection(environmentText))
            .addBlocks(SlackBuilderFE.Divider())
            .send();
    };
    calcMeta = (item) => {
        if (!item.gender && !item.age)
            return 'N/A';
        let meta = '';
        if (item.gender) {
            const gender = ModuleFE_Value.cache.unique(item.gender);
            if (!gender)
                this.logError(`Could not get value for id ${item.gender}`);
            else
                meta += `${resolveValueDisplay(gender)}`;
        }
        if (item.age) {
            if (meta.length)
                meta += ' - ';
            meta += item.age;
        }
        return meta;
    };
    onClickEncounter = async (encounter, isExpired) => {
        if (isExpired) {
            ModuleFE_Toaster.toastError('Session is expired, please click the "Refresh" button bellow');
            return;
        }
        if (!encounter.sessionJwt || !JWT.isValid(encounter.sessionJwt))
            encounter.sessionJwt = (await ModuleFE_AvatarMode._v1.getAvatarLaunchJwt({ token: encounter.jwt }).executeSync()).jwt;
        this.setState({ selectedEncounter: encounter });
    };
    //######################### Render #########################
    render() {
        const { leftPanelCollapsed } = this.state;
        return _jsxs(LL_H_C, { id: 'page__avatar-mode', children: [_jsxs("div", { className: _className('avatar-mode__sidebar-wrapper', leftPanelCollapsed && 'is-collapsed'), children: [_jsx("div", { className: _className('avatar-mode__encounters-container', leftPanelCollapsed && 'collapsed'), children: _jsxs("div", { className: 'avatar-mode__encounters-inner', children: [this.render_Filters(), this.render_EncounterList(), this.render_loadEncounterListButton()] }) }), _jsx("button", { className: 'avatar-mode__sidebar-toggle', onClick: this.toggleLeftPanelCollapsed, "aria-label": leftPanelCollapsed ? 'Expand encounters menu' : 'Collapse encounters menu', children: _jsx("div", { className: 'avatar-mode__sidebar-toggle__icon', children: _jsx("svg", { viewBox: '0 0 11 11', children: _jsx("polyline", { points: '7,1.5 3,5.5 7,9.5' }) }) }) })] }), _jsx("div", { className: 'avatar-mode__right-panel', children: this.state.selectedEncounter && (_jsx(ResizableWrapper, { className: 'avatar-mode__advisor-frame-resizable', storageKeySuffix: 'avatar-mode-advisor-frame', children: this.render_Frame(this.state.selectedEncounter) })) })] });
    }
    render_Filters = () => {
        const { selectedFacilityIds, facilityIds, activeEncountersOnly } = this.state;
        const hasFilters = selectedFacilityIds?.length !== facilityIds?.length || activeEncountersOnly;
        const filtersClassName = _className('avatar-mode__filters__filter', hasFilters && 'filter-active');
        return _jsxs("div", { className: 'avatar-mode__filters', children: [_jsxs("div", { className: 'avatar-mode__filters__search-bar', children: [_jsx(ICONS.advisor_v5_search.component, {}), _jsx(TS_InputV2, { type: "text", value: this.state.search, onChange: (value) => this.setState({ search: value }), onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    this.loadEncounters(this.state.search?.trim() || undefined);
                            }, placeholder: "Search Encounter Id", className: 'avatar-mode__search-bar__input', style: { fontFamily: 'monospace', fontSize: 15 } })] }), _jsx(ICONS.advisor_avn_filter.component, { className: filtersClassName, ...openContent.popUpV2.bottom({
                        content: _jsx(Dialog_AvatarModeFilters, { encounters: this.state.encounters, selectedFacilityIds: this.state.selectedFacilityIds, activeEncountersOnly: this.state.activeEncountersOnly, onActiveEncountersChange: (checked) => this.setState({ activeEncountersOnly: checked }) }),
                        id: 'avatar-mode__filters_popup'
                    }) })] });
    };
    render_loadEncounterListButton = () => {
        return _jsx(Button, { variant: 'primary', className: 'avatar-mode__reload-button', onClick: () => this.loadEncounters(), children: "Refresh" });
    };
    render_EncounterList = () => {
        const encounters = this.getEncounters();
        return _jsx(FadeScrollWrapper, { className: 'avatar-mode__encounter-list', children: encounters.map(this.render_EncounterList_Item) });
    };
    render_EncounterList_Item = (encounter) => {
        const selectedEncounter = this.state.selectedEncounter;
        const selected = encounter.encounterId === selectedEncounter?.encounterId;
        const rootJwtValid = JWT.isValid(encounter.jwt);
        const sessionJwtValid = JWT.isValid(encounter.sessionJwt);
        const isExpired = !rootJwtValid && !sessionJwtValid;
        const needsRelaunch = selected && rootJwtValid && !sessionJwtValid;
        const className = _className('avatar-mode__encounter-list__item', selected ? 'selected' : isExpired && 'expired-encounter');
        const ehrInstance = ModuleFE_EHRInstance.cache.unique(encounter.ehrInstanceId);
        const facility = ModuleFE_Facility.cache.unique(encounter.facilityId);
        const environment = ModuleFE_Environment.cache.unique(encounter.environmentId);
        const installation = ModuleFE_EHRInstallation.cache.unique(environment?.ehrInstallationId);
        const organizationId = installation?.organizationId || ehrInstance?.organizationId;
        const organization = ModuleFE_Organization.cache.unique(organizationId);
        const meta = this.calcMeta(encounter);
        const chiefComplaints = filterDuplicates(encounter.chiefComplaints.map(chiefComplaints => capitalizeAllFirstLetters(chiefComplaints)));
        const physicianNames = filterDuplicates((encounter.physicianNames ?? []).map(name => capitalizeAllFirstLetters(name)));
        return _jsxs(LL_V_L, { className: className, children: [_jsx(TS_PropRenderer.Horizontal, { label: 'Encounter ID:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value clickable', onClick: e => {
                            if (isExpired)
                                return ModuleFE_Toaster.toastError('Session is expired, please click the "Refresh" button bellow');
                            stopPropagation(e);
                            ModuleFE_Thunderstorm.copyToClipboard(encounter.encounterId);
                        }, children: encounter.encounterId }) }), _jsx(TS_PropRenderer.Horizontal, { label: 'Organization:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: organization?.name }) }), environment && (_jsx(TS_PropRenderer.Horizontal, { label: 'Environment:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: `${installation?.label} - ${environment.label}` }) })), facility && (_jsx(TS_PropRenderer.Horizontal, { label: 'Facility:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: facility.name }) })), ehrInstance && (_jsx(TS_PropRenderer.Horizontal, { label: 'EHR Instance:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: ehrInstance.label }) })), chiefComplaints.length > 0 && (_jsx(TS_PropRenderer.Horizontal, { label: 'Chief Complaints:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: chiefComplaints.join(', ') }) })), physicianNames.length > 0 && (_jsx(TS_PropRenderer.Horizontal, { label: 'Physicians:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: physicianNames.join(', ') }) })), _jsx(TS_PropRenderer.Horizontal, { label: 'Started At:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: formatTimestamp('DD/MM/YYYY - HH:mmA', encounter.startTime) }) }), _jsx(TS_PropRenderer.Horizontal, { label: 'Meta:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: meta }) }), _jsx(TS_PropRenderer.Horizontal, { label: 'Status:', children: _jsx("div", { className: `avatar-mode__encounter-list__item__status ${encounter.isAlive ? 'alive' : 'dead'}` }) }), encounter.encounterStatus && (_jsx(TS_PropRenderer.Horizontal, { label: 'Encounter Status:', children: _jsx("div", { className: 'avatar-mode__encounter-list__item__value', children: capitalizeAllFirstLetters(encounter.encounterStatus) }) })), !selected && !isExpired && this.render_EncounterActionButton(encounter, isExpired, {
                    tooltipId: 'avatar-mode-send-button-tooltip',
                    tooltipText: 'launch encounter',
                    errorMessage: 'Could not launch avatar encounter. Please try again.',
                    Icon: ICONS.advisor_v4_sendArrow.component,
                    modifier: 'launch',
                }), needsRelaunch && this.render_EncounterActionButton(encounter, isExpired, {
                    tooltipId: 'avatar-mode-relaunch-button-tooltip',
                    tooltipText: 'Session expired — relaunch encounter',
                    errorMessage: 'Could not relaunch encounter. Please try again.',
                    Icon: ICONS.sync.component,
                    modifier: 'relaunch',
                })] }, encounter.encounterId);
    };
    render_EncounterActionButton = (encounter, isExpired, config) => {
        const tooltipProps = openContent.tooltip.top(config.tooltipId, config.tooltipText);
        const { Icon } = config;
        const baseClass = 'avatar-mode__encounter-list__item__send-button';
        return _jsx(Button, { className: _className(baseClass, `${baseClass}--${config.modifier}`), variant: 'tertiary', onClick: async () => {
                ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip);
                try {
                    await this.onClickEncounter(encounter, isExpired);
                }
                catch (error) {
                    ModuleFE_Toaster.toastError(config.errorMessage);
                }
            }, onMouseEnter: tooltipProps.onMouseEnter, onMouseLeave: tooltipProps.onMouseLeave, children: _jsx(Icon, {}) });
    };
    render_Frame = (encounterToLaunch) => {
        const env = Thunder.getInstance().getEnvironment().toLowerCase();
        let url = advisorBaseUrl[env];
        url += '/launch-avatar?';
        url += `${QueryParam_SessionId}=${encounterToLaunch.sessionJwt}`;
        url += `&${UrlParam_UserType}=avatar-mode`;
        return _jsx("iframe", { className: 'match_parent', src: url, allow: "clipboard-write" }, url);
    };
}
//# sourceMappingURL=Page_AvatarMode.js.map